import {isPassportIdentity} from '@sollapay/domain';
import {PartyType} from '@sollapay/enums';
import {HeaderMetaRow} from '@sollapay/ui/components';
import {formatDateLocalized, formatPhoneDisplay} from '@sollapay/utils';
import {useTranslation} from 'react-i18next';

import {getBuyerIdNumber} from '@/features/buyers/utils';

import type {BuyerWithDocumentsDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface BuyerMetaInfoProps {
  buyer: BuyerWithDocumentsDTO;
}

export const BuyerMetaInfo: FC<BuyerMetaInfoProps> = ({buyer}) => {
  const {t, i18n} = useTranslation('trustAccounts');

  // Corporate buyers: show company ID + incorporation date, and the primary signatory's
  // contact details (all communication goes to the signatory).
  if (buyer.partyType === PartyType.ORGANIZATION) {
    const primarySignatory = buyer.signatories?.find(s => s.isPrimary) ?? buyer.signatories?.[0];
    const incorporationDate = buyer.organization?.incorporationDate;

    return (
      <HeaderMetaRow
        items={[
          {
            icon: 'calendar',
            tooltip: t('view.buyers.detail.metaInfo.incorporationDate'),
            value: incorporationDate ? formatDateLocalized(incorporationDate, i18n.language) : null
          },
          {
            icon: 'shield',
            tooltip: t('view.buyers.detail.metaInfo.companyId'),
            value: getBuyerIdNumber(buyer, i18n.language)
          },
          {
            icon: 'user',
            tooltip: t('view.buyers.detail.metaInfo.signatory'),
            value: primarySignatory?.fullName ?? null
          },
          {
            icon: 'phone',
            tooltip: t('view.buyers.detail.metaInfo.phone'),
            value: primarySignatory?.phone ? formatPhoneDisplay(primarySignatory.phone) : null
          },
          {
            icon: 'mail',
            tooltip: t('view.buyers.detail.metaInfo.email'),
            value: primarySignatory?.email ?? null
          },
          {
            icon: 'marker-pin',
            tooltip: t('view.buyers.detail.metaInfo.address'),
            value: buyer.address
          }
        ]}
      />
    );
  }

  const isPassport = isPassportIdentity(buyer.idType);

  return (
    <HeaderMetaRow
      items={[
        {
          icon: 'calendar',
          tooltip: t('view.buyers.detail.metaInfo.dateOfBirth'),
          value: buyer.dateOfBirth ? formatDateLocalized(buyer.dateOfBirth, i18n.language) : null
        },
        {
          icon: isPassport ? 'passport' : 'shield',
          tooltip: isPassport
            ? t('common:identity.passportLabel')
            : t('view.buyers.detail.metaInfo.nationalId'),
          value: getBuyerIdNumber(buyer, i18n.language)
        },
        {
          icon: 'phone',
          tooltip: t('view.buyers.detail.metaInfo.phone'),
          value: buyer.phone ? formatPhoneDisplay(buyer.phone) : null
        },
        {
          icon: 'mail',
          tooltip: t('view.buyers.detail.metaInfo.email'),
          value: buyer.email
        },
        {
          icon: 'marker-pin',
          tooltip: t('view.buyers.detail.metaInfo.address'),
          value: buyer.address
        }
      ]}
    />
  );
};
